import { useEffect, useRef, useState } from 'react';
import i18n from '@/i18n/config';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { RiEyeLine, RiEyeOffLine, RiArrowLeftLine } from '@remixicon/react';
import { Button, InputOtp, Input, Form } from '@heroui/react';
import { z } from 'zod';

import { apiClient } from '@/utils/api';
import { setSession } from '@/utils/auth';
import { isAuthenticatedAtom, tokenAtom, profileAtom } from '@/store';

const englishFieldClassNames = {
  input: 'text-left',
  inputWrapper: 'flex-row'
};

export default function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setToken = useSetAtom(tokenAtom);
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const setProfile = useSetAtom(profileAtom);

  const [isVisible, setIsVisible] = useState(false);
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const hasAutoSubmittedRef = useRef(false);

  const toggleVisibility = () => setIsVisible(!isVisible);

  // Submit state is managed by tanstack form via form.Subscribe

  const form = useForm({
    // Initial form values
    defaultValues: {
      email: '',
      password: ''
    },
    // The function to call on submission
    onSubmit: async ({ value }) => {
      if (!isOtpStep) {
        try {
          await apiClient.post('/login', {
            email: value.email,
            password: value.password
          });
          setOtp('');
          setIsOtpStep(true);
        } catch (error) {
          // Error is already handled by axios interceptor
          // No need to call ErrorHandler.handle again
        }

        return;
      }

      try {
        const { data } = await apiClient.post('/verify-otp', {
          email: value.email,
          otp
        });

        const tokenFromData = data?.data?.token || data?.token || null;

        if (tokenFromData) {
          setToken(tokenFromData);
          setSession(tokenFromData);
          setIsAuthenticated(true);

          // Invalidate profile query to fetch fresh profile after login
          setProfile({ type: 'refetch' });
        }

        const redirect = searchParams.get('redirect');

        navigate(redirect || '/', {
          replace: true
        });
      } catch (error) {
        // Error is already handled by axios interceptor
        // No need to call ErrorHandler.handle again
      }
    }
    // Validation is handled per-field using Zod
  });

  // Auto-submit when OTP is 6 digits in OTP step
  useEffect(() => {
    if (!isOtpStep) {
      hasAutoSubmittedRef.current = false;

      return;
    }
    if (otp.length === 6 && !hasAutoSubmittedRef.current) {
      hasAutoSubmittedRef.current = true;
      form.handleSubmit();
    }
    if (otp.length < 6) {
      hasAutoSubmittedRef.current = false;
    }
  }, [isOtpStep, otp, form]);

  return (
    <Form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {/* OTP Step */}
      {isOtpStep ? (
        <div className="mx-auto w-full space-y-4">
          <div className="flex items-center">
            <button
              className="text-default-500 hover:text-foreground inline-flex items-center gap-2 text-sm"
              type="button"
              onClick={() => setIsOtpStep(false)}
            >
              <RiArrowLeftLine className="h-5 w-5" />{i18n.t('common.back')}</button>
          </div>

          <div className="space-y-1 text-center">
            <form.Subscribe selector={(state) => state.values.email}>
              {(email) => (
                <>
                  <div className="label justify-center">
                    <span className="label-text">{i18n.t('auth.otpSentTo', { email })}</span>
                  </div>
                  <p className="text-default-400 text-xs">{i18n.t('auth.otpHint')}</p>
                </>
              )}
            </form.Subscribe>
          </div>
          <div className="flex justify-center" dir="ltr">
            <InputOtp
              isRequired
              aria-label={i18n.t('auth.otpAria')}
              classNames={{
                base: 'direction-ltr',
                segment: 'text-left'
              }}
              length={6}
              name="otp"
              placeholder={i18n.t('auth.enterCode')}
              size="lg"
              value={otp}
              onValueChange={setOtp}
            />
          </div>
        </div>
      ) : (
        <>
          {/* Email Field */}
          <form.Field
            name="email"
            validators={{
              onChange: z.string().email(i18n.t('auth.invalidEmail'))
            }}
          >
            {(field) => {
              // Helper to extract error message safely
              const getErrorMessage = () => {
                if (!field.state.meta.errors || field.state.meta.errors.length === 0) {
                  return undefined;
                }
                const error = field.state.meta.errors[0];
                // Handle string errors
                if (typeof error === 'string') {
                  return error;
                }
                // Handle object errors (Zod errors have a message property)
                if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                  return error.message;
                }
                // Fallback: convert to string
                return String(error);
              };

              return (
                <Input
                  isClearable
                  classNames={englishFieldClassNames}
                  dir="ltr"
                  errorMessage={getErrorMessage()}
                  isDisabled={isOtpStep}
                  isInvalid={Boolean(field.state.meta.errors?.length)}
                  label={i18n.t('auth.email')}
                  labelPlacement="outside"
                  placeholder={i18n.t('auth.enterEmail')}
                  size="lg"
                  type="email"
                  value={field.state.value}
                  variant="bordered"
                  onBlur={field.handleBlur}
                  onClear={() => field.setValue('')}
                  onValueChange={(val) => field.handleChange(val)}
                />
              );
            }}
          </form.Field>

          {/* Password Field */}
          <form.Field
            name="password"
            validators={{
              onChange: z.string().min(6, i18n.t('auth.passwordMinLength'))
            }}
          >
            {(field) => {
              // Helper to extract error message safely
              const getErrorMessage = () => {
                if (!field.state.meta.errors || field.state.meta.errors.length === 0) {
                  return undefined;
                }
                const error = field.state.meta.errors[0];
                // Handle string errors
                if (typeof error === 'string') {
                  return error;
                }
                // Handle object errors (Zod errors have a message property)
                if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                  return error.message;
                }
                // Fallback: convert to string
                return String(error);
              };

              return (
                <Input
                  classNames={englishFieldClassNames}
                  dir="ltr"
                  endContent={
                    <button
                      aria-label={i18n.t('auth.togglePasswordVisibility')}
                      aria-pressed={isVisible}
                      className="outline-transparent focus:outline-solid"
                      title={isVisible ? i18n.t('auth.hidePassword') : i18n.t('auth.showPassword')}
                      type="button"
                      onClick={toggleVisibility}
                    >
                      {isVisible ? (
                        <RiEyeOffLine className="text-default-400 pointer-events-none h-5 w-5" />
                      ) : (
                        <RiEyeLine className="text-default-400 pointer-events-none h-5 w-5" />
                      )}
                    </button>
                  }
                  errorMessage={getErrorMessage()}
                  isInvalid={Boolean(field.state.meta.errors?.length)}
                  label={i18n.t('auth.password')}
                  labelPlacement="outside"
                  placeholder={i18n.t('auth.enterPassword')}
                  size="lg"
                  type={isVisible ? 'text' : 'password'}
                  value={field.state.value}
                  variant="bordered"
                  onBlur={field.handleBlur}
                  onValueChange={(val) => field.handleChange(val)}
                />
              );
            }}
          </form.Field>
        </>
      )}

      <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
        {([canSubmit, isSubmitting]) => (
          <Button
            className="w-full"
            color="primary"
            disabled={!canSubmit || (isOtpStep && otp.length !== 6)}
            isLoading={isSubmitting}
            size="lg"
            type="submit"
          >
            {isOtpStep ? i18n.t('auth.verifyOtp') : i18n.t('auth.login')}
          </Button>
        )}
      </form.Subscribe>

      {false && isOtpStep && (
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => {
            setIsOtpStep(false);
            setOtp('');
          }}
        >{i18n.t('auth.backToCredentials')}</button>
      )}
    </Form>
  );
}
